/**
 * Migration 0102 — staff-side read-only household panel on customer detail,
 * plus an "Invite co-owner" action (customers.edit) that fires the same
 * `customer.household_invite` email the primary would.
 */
import { Users } from 'lucide-react';
import { type FormEvent, useCallback, useEffect, useState } from 'react';
import {
  ApiError,
  type StaffHouseholdMember as HouseholdMember,
  type StaffHouseholdResponse as HouseholdResponse,
  getCustomerHousehold,
  inviteHouseholdMember,
} from '../../lib/api';

function permissionSummary(p: HouseholdMember['permissions']): string {
  const parts: string[] = [];
  if (p.canBook) parts.push('book');
  if (p.canPay) parts.push('pay');
  if (p.canEditPets) parts.push('edit pets');
  if (p.receivesNotifications) parts.push('emails');
  return parts.length === 4
    ? 'Full access'
    : parts.length === 0
      ? 'View only'
      : `Can ${parts.join(', ')}`;
}

export function HouseholdSection({
  customerId,
  canEdit,
}: { customerId: string; canEdit: boolean }) {
  const [data, setData] = useState<HouseholdResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await getCustomerHousehold(customerId);
      setData(res);
      setError(null);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? `Could not load household (${err.status})`
          : 'Could not load household',
      );
    }
  }, [customerId]);

  useEffect(() => {
    void load();
  }, [load]);

  const invite = async (e: FormEvent) => {
    e.preventDefault();
    setSending(true);
    setError(null);
    setNotice(null);
    try {
      const res = await inviteHouseholdMember(customerId, email);
      setNotice(
        res.reused
          ? `Invitation re-sent to ${res.invitedEmail}.`
          : `Invitation sent to ${res.invitedEmail}.`,
      );
      setEmail('');
      setInviteOpen(false);
      await load();
    } catch (err) {
      const body =
        err instanceof ApiError ? (err.body as { message?: string; error?: string } | null) : null;
      setError(
        body?.message ??
          (body?.error === 'household_has_no_primary'
            ? 'This customer has not signed in to the portal yet.'
            : 'Could not send the invitation.'),
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="mt-6" aria-labelledby="household-heading">
      <div className="flex min-h-11 items-center justify-between gap-3">
        <h2 id="household-heading" className="flex items-center gap-2 text-sm font-medium">
          <Users size={16} aria-hidden="true" />
          Household
        </h2>
        {canEdit && data?.hasPrimary ? (
          <button
            type="button"
            className="min-h-11 rounded-md border border-[var(--border)] px-2.5 py-1 text-xs md:min-h-0"
            onClick={() => setInviteOpen((v) => !v)}
          >
            Invite co-owner
          </button>
        ) : null}
      </div>
      {error ? (
        <p role="alert" className="mt-2 text-xs text-[var(--destructive)]">
          {error}
        </p>
      ) : null}
      {notice ? (
        <p role="status" className="mt-2 text-xs text-[var(--muted-foreground)]">
          {notice}
        </p>
      ) : null}
      {data === null && !error ? (
        <p className="mt-2 text-xs text-[var(--muted-foreground)]">Loading…</p>
      ) : data && !data.hasPrimary ? (
        <p className="mt-2 text-xs text-[var(--muted-foreground)]">
          No portal login yet. The first person to sign in with this customer's email becomes the
          household's primary contact.
        </p>
      ) : data ? (
        <ul className="mt-2 divide-y divide-[var(--border)]">
          {data.members.map((m) => (
            <li
              key={m.id}
              className="flex flex-wrap items-baseline justify-between gap-x-3 py-2 text-sm"
            >
              <span>
                {m.name ?? m.email ?? 'Invited member'}
                {m.name && m.email ? (
                  <span className="ml-1 text-xs text-[var(--muted-foreground)]">{m.email}</span>
                ) : null}
              </span>
              <span className="text-xs text-[var(--muted-foreground)]">
                {m.role === 'primary'
                  ? 'Primary contact'
                  : m.status === 'pending'
                    ? 'Invited — not yet accepted'
                    : permissionSummary(m.permissions)}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
      {inviteOpen ? (
        <form onSubmit={invite} className="mt-3 flex flex-wrap items-end gap-2">
          <label className="flex w-full min-w-0 flex-col text-xs sm:w-auto">
            Co-owner email
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 min-h-11 w-full rounded-md border border-[var(--border)] px-2 py-1 text-sm md:min-h-0"
            />
          </label>
          <button
            type="submit"
            disabled={sending}
            className="min-h-11 rounded-md bg-[var(--primary)] px-3 py-1.5 text-xs font-medium text-white md:min-h-0"
          >
            {sending ? 'Sending…' : 'Send invitation'}
          </button>
        </form>
      ) : null}
    </section>
  );
}
